import type { SolvedArc } from './solver.model';

/**
 * Solve an arc given numeric values.
 *
 * Required: `r` (radius), `startAngle` (degrees).
 * One of: `endAngle` (degrees) or `centralAngle` (degrees).
 * Optional: `cx`, `cy` for center position (default origin).
 *
 * All input angles are in **degrees**. The solver converts to radians internally.
 * The arc goes counterclockwise from startAngle to endAngle.
 */
export function solveArc(values: Record<string, number>): SolvedArc {
  const r = values['r'];
  if (r === undefined || r <= 0) {
    throw new Error(`Arc radius must be positive: r=${r}`);
  }

  const startAngleDeg = values['startAngle'] ?? 0;

  let centralAngleDeg: number;
  if (values['centralAngle'] !== undefined) {
    centralAngleDeg = values['centralAngle'];
  } else if (values['endAngle'] !== undefined) {
    centralAngleDeg = values['endAngle'] - startAngleDeg;
  } else {
    throw new Error('Arc requires either centralAngle or endAngle');
  }

  // Normalize central angle to (0, 360]
  centralAngleDeg = ((centralAngleDeg % 360) + 360) % 360;
  if (centralAngleDeg === 0) {
    centralAngleDeg = 360;
  }

  const endAngleDeg = startAngleDeg + centralAngleDeg;

  const cx = values['cx'] ?? 0;
  const cy = values['cy'] ?? 0;

  const startAngleRad = (startAngleDeg * Math.PI) / 180;
  const endAngleRad = (endAngleDeg * Math.PI) / 180;
  const centralAngleRad = (centralAngleDeg * Math.PI) / 180;

  const arcLength = r * centralAngleRad;
  const chordLength = 2 * r * Math.sin(centralAngleRad / 2);
  const sectorArea = 0.5 * r * r * centralAngleRad;
  const segmentArea = 0.5 * r * r * (centralAngleRad - Math.sin(centralAngleRad));

  const midAngleRad = (startAngleRad + endAngleRad) / 2;

  const startPoint: [number, number] = [r * Math.cos(startAngleRad), r * Math.sin(startAngleRad)];
  const endPoint: [number, number] = [r * Math.cos(endAngleRad), r * Math.sin(endAngleRad)];
  const midPoint: [number, number] = [r * Math.cos(midAngleRad), r * Math.sin(midAngleRad)];

  return {
    center: [cx, cy],
    radius: r,
    startAngleDeg,
    endAngleDeg,
    centralAngleDeg,
    startAngleRad,
    endAngleRad,
    centralAngleRad,
    arcLength,
    chordLength,
    sectorArea,
    segmentArea,
    startPoint,
    endPoint,
    midPoint,
  };
}
